import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { OpenInNew, type SvgIconComponent } from '@mui/icons-material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Icon as MuiIcon,
} from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { BetaBadge } from '@ovComponents/3-pattern/betaBadge/betaBadge';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { usePermissions } from '@providers/userContextProvider';
import { kebabCase } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge } from '../badge/badge';
import { useNavSectionTokens } from './navSection.tokens';

export type SubSection = {
  name: string | TranslatedString;
  path: string;
  accessKey?: string;
  permissions?: string[];
  configurableTablePage?: boolean;
  beta?: boolean;
  newTab?: boolean;
  /** When true, only highlight on an exact path match (not nested routes). */
  matchPathExactly?: boolean;
  stats?:
    | 'flagsCount'
    | 'subAccountsToReviewCount'
    | 'complianceReviewCount'
    | 'alertsCount'
    | 'statementsToReviewCount'
    | 'annualSuitabilityReviewCount'
    | 'bankAccountAwaitingReviewCount'
    | 'institutionAwaitingReviewCount';
};

export type Section = {
  name: string | TranslatedString;
  icon: SvgIconComponent | string;
  subSections: SubSection[];
  beta?: boolean;
  newTab?: boolean;
  /** Keep parent/child nav labels when there is only one sub-section. */
  forceExpandable?: boolean;
};

export const getSubSectionName = (subSectionName: SubSection['name']) =>
  typeof subSectionName === 'string'
    ? subSectionName
    : translateBackend(subSectionName);

export const isNavSubSectionPathActive = (
  sub: Pick<SubSection, 'path' | 'matchPathExactly'>,
  pathname: string,
): boolean => {
  if (sub.path === '/') {
    return pathname === '/';
  }
  if (sub.matchPathExactly) {
    return pathname === sub.path;
  }
  return pathname === sub.path || pathname.startsWith(`${sub.path}/`);
};

export const NavSection = ({
  section,
  accessiblePages,
  handleDrawerClick,
  globalStats,
  displayStats,
  showSection,
  setSectionsOpen,
  sectionsOpen,
  index,
  location,
  closed,
  openedMenu,
}: {
  section: Section;
  accessiblePages: any[];
  handleDrawerClick: (x: string) => void;
  globalStats: any;
  displayStats: (x: SubSection) => boolean;
  showSection: boolean;
  setSectionsOpen: (x: boolean[]) => void;
  openedMenu: boolean;
  sectionsOpen: boolean[];
  index: number;
  location: any;
  closed: boolean;
}) => {
  const { t } = useTranslation(['navMenu']);
  const { permissions } = usePermissions();
  const pageIsAccessible = (
    key?: string,
    permissionsRequired?: string[],
  ): boolean =>
    (!key && !permissionsRequired) ||
    accessiblePages.includes(key) ||
    (permissionsRequired?.some((x) => permissions.includes(x)) ?? false);
  const { comp, sys } = useThemeTokens(useNavSectionTokens());
  const [localClosed, setLocalClosed] = useState<boolean>(closed);

  useEffect(() => {
    if (closed) {
      setLocalClosed(closed);
    } else {
      setTimeout(() => {
        setLocalClosed(closed);
      }, 150);
    }
  }, [closed]);

  const buttonStyle = {
    padding: '12px 12px',
    color: comp.navSection.color,
    borderRadius: comp.navSection.radius,
    '&:hover,&.Mui-selected': {
      backgroundColor: comp.navSection.selectedColor,
    },
    '&:hover&.Mui-selected': { backgroundColor: comp.navSection.selectedColor },
    '&:hover': { backgroundColor: comp.navSection.hoverColor },
  };

  const sectionActive = () =>
    section.subSections
      .filter(
        (x: SubSection) =>
          !x.accessKey || pageIsAccessible(x.accessKey, x.permissions),
      )
      .some((x: SubSection) => isNavSubSectionPathActive(x, location.pathname));

  const menuPopout = () => (
    <Box
      sx={{
        position: 'absolute',
        top: '0px',
        left: '100%',
        display: {
          xs: 'none',
          lg: openedMenu && closed ? 'block' : 'none',
        },
        background: comp.navSection.backgroundColor,
        p: '4px',
        borderRadius: '8px',
        boxShadow: sys.elevation.high,
        minWidth: '200px',
      }}
    >
      {section.subSections
        .filter((y: SubSection) => pageIsAccessible(y.accessKey, y.permissions))
        .map((sub: SubSection) => (
          <ListItem
            disablePadding
            key={getSubSectionName(sub.name)}
            component={Link}
            to={sub.path}
            target={sub.newTab ? '_blank' : '_self'}
            data-testid={kebabCase(getSubSectionName(sub.name) ?? '')}
            onClick={() => handleDrawerClick(getSubSectionName(sub.name) ?? '')}
            secondaryAction={
              displayStats(sub) && (
                <Badge
                  label={
                    globalStats[sub.stats!] > 999
                      ? '999+'
                      : globalStats[sub.stats!].toString()
                  }
                  color='negative'
                  variant='number'
                />
              )
            }
          >
            <ListItemButton
              sx={{ ...buttonStyle }}
              selected={isNavSubSectionPathActive(sub, location.pathname)}
            >
              <Typography
                variant='bodySmall'
                weight={
                  isNavSubSectionPathActive(sub, location.pathname)
                    ? 'bold'
                    : 'regular'
                }
              >
                {typeof sub.name === 'string'
                  ? t(sub.name)
                  : translateBackend(sub.name)}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
    </Box>
  );

  const subSections = section.subSections.filter((x: SubSection) =>
    pageIsAccessible(x.accessKey, x.permissions),
  );
  if (subSections.length === 0) {
    return <></>;
  }
  if (subSections.length === 1 && !section.forceExpandable) {
    const s0 = subSections[0];
    const title = showSection
      ? getSubSectionName(section.name)
      : getSubSectionName(s0.name);
    return (
      <Box
        key={getSubSectionName(section.name)}
        pl={0.5}
        pr={0.5}
        position='relative'
        mb={0.25}
      >
        <ListItem
          key={
            typeof s0.name === 'string' ? (t(s0.name) as string) : s0.name.en
          }
          component={Link}
          to={s0.path}
          target={s0.newTab || section.newTab ? '_blank' : '_self'}
          data-testid={kebabCase(getSubSectionName(s0.name) ?? '')}
          onClick={() => handleDrawerClick(getSubSectionName(s0.name) ?? '')}
          disablePadding
          secondaryAction={
            displayStats(s0) && (
              <Badge
                label={
                  globalStats[s0.stats!] > 999
                    ? '999+'
                    : globalStats[s0.stats!].toString()
                }
                color='negative'
                variant='number'
              />
            )
          }
        >
          <ListItemButton
            selected={isNavSubSectionPathActive(s0, location.pathname)}
            sx={buttonStyle}
          >
            <Box
              display='flex'
              justifyContent='start'
              alignItems='center'
              width='100%'
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {typeof section.icon === 'string' ? (
                  <MuiIcon
                    sx={{
                      color: comp.navSection.color,
                      fontSize: '16px !important',
                      p: '2px',
                    }}
                  >
                    {section.icon}
                  </MuiIcon>
                ) : (
                  <Icon
                    icon={section.icon as SvgIconComponent}
                    sx={{ color: comp.navSection.color, p: '2px' }}
                    size='small'
                  />
                )}
              </ListItemIcon>
              {!localClosed && (
                <Box
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  width='100%'
                >
                  <Typography variant='bodyMedium'>
                    {typeof title === 'string'
                      ? t(title)
                      : translateBackend(title)}
                  </Typography>
                  <Box
                    display='flex'
                    justifyContent='end'
                    alignItems='center'
                    gap={1}
                  >
                    {section.beta && (
                      <BetaBadge sx={{ ml: 1 }} variant='small' />
                    )}
                    {section.newTab && <OpenInNew sx={{ fontSize: '16px' }} />}
                  </Box>
                </Box>
              )}
            </Box>
          </ListItemButton>
        </ListItem>
        {menuPopout()}
      </Box>
    );
  }
  return (
    <Box
      key={getSubSectionName(section.name)}
      pl={0.5}
      pr={0.5}
      position='relative'
      overflow='visible'
      mb={0.25}
    >
      <ListItemButton
        onClick={() =>
          setSectionsOpen(
            sectionsOpen.map((val, idx) => (idx === index ? !val : val)),
          )
        }
        sx={buttonStyle}
        data-testid={`${kebabCase(getSubSectionName(section.name) ?? '')}-nav-section`}
        selected={sectionActive() && localClosed}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          width='100%'
        >
          <Box display='flex' justifyContent='start' alignItems='center'>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {typeof section.icon === 'string' ? (
                <MuiIcon
                  sx={{
                    color: comp.navSection.color,
                    fontSize: '16px !important',
                    p: '2px',
                  }}
                >
                  {section.icon}
                </MuiIcon>
              ) : (
                <Icon
                  icon={section.icon as SvgIconComponent}
                  sx={{ color: comp.navSection.color, p: '2px' }}
                  size='small'
                />
              )}
            </ListItemIcon>
            {!localClosed && (
              <Typography variant='bodyMedium'>
                {typeof section.name === 'string'
                  ? t(section.name)
                  : translateBackend(section.name)}
              </Typography>
            )}
          </Box>
          {section.newTab ? (
            <OpenInNew sx={{ fontSize: '16px' }} />
          ) : (
            !localClosed &&
            (sectionsOpen[index] ? (
              <ExpandLess sx={{ fontSize: '16px' }} />
            ) : (
              <ExpandMore sx={{ fontSize: '16px' }} />
            ))
          )}
        </Box>
      </ListItemButton>
      <Collapse
        in={sectionsOpen[index] && !localClosed}
        timeout='auto'
        unmountOnExit
      >
        <List component='div' sx={{ padding: 0 }}>
          {section.subSections
            .filter((y: SubSection) =>
              pageIsAccessible(y.accessKey, y.permissions),
            )
            .map((sub: SubSection) => (
              <ListItem
                sx={{ mb: 0.25 }}
                disablePadding
                key={getSubSectionName(sub.name)}
                component={Link}
                to={sub.path}
                target={sub.newTab ? '_blank' : '_self'}
                data-testid={kebabCase(getSubSectionName(sub.name) ?? '')}
                onClick={() =>
                  handleDrawerClick(getSubSectionName(sub.name) ?? '')
                }
                secondaryAction={
                  displayStats(sub) && (
                    <Badge
                      label={
                        globalStats[sub.stats!] > 999
                          ? '999+'
                          : globalStats[sub.stats!].toString()
                      }
                      color='negative'
                      variant='number'
                    />
                  )
                }
              >
                <ListItemButton
                  sx={{ ...buttonStyle }}
                  selected={isNavSubSectionPathActive(sub, location.pathname)}
                >
                  <Typography
                    variant='bodySmall'
                    weight={
                      isNavSubSectionPathActive(sub, location.pathname)
                        ? 'bold'
                        : 'regular'
                    }
                  >
                    {typeof sub.name === 'string'
                      ? t(sub.name)
                      : translateBackend(sub.name)}
                  </Typography>
                  {sub.beta && <BetaBadge sx={{ ml: 1 }} variant='small' />}
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Collapse>
      {menuPopout()}
    </Box>
  );
};
